import { useEffect, useState } from 'react';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { LoadingButton } from '@mui/lab';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as pdfjsLib from 'pdfjs-dist';
import Typography from '@mui/material/Typography';
import UploadDocumentDialog from '@/components/common/documents-box/upload-document-dialog';
import Lightbox from 'react-image-lightbox';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.min.mjs';

const isPdf = (name: string) => name.split('.').reverse()[0] === 'pdf';

interface Props {
    label: string;
    file: any;
    isLoadingFile: boolean;
    tag?: string;
    refetch?: any;
    subLabel?: string;
    withUploadButton?: boolean;
}

const DocumentBoxItem = ({ label, file, isLoadingFile, tag, refetch, subLabel, withUploadButton = true }: Props) => {
    const [lightBoxState, setLightBoxState] = useState<boolean>(false);
    const [imageIndex, setImageIndex] = useState<number>(0);
    const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
    const [pdf, setPdf] = useState<any>();
    const [images, setImages] = useState<any[]>([]);
    const [uploadDocumentDialogState, setUploadDocumentDialogState] = useState<boolean>(false);

    const handleUploadDocumentDialogOpen = () => {
        setUploadDocumentDialogState(true);
    };

    const handleUploadDocumentDialogClose = () => {
        setUploadDocumentDialogState(false);
    };

    const getImages = async () => {
        const imagesList = [];
        const canvas = document.createElement('canvas');
        canvas.setAttribute('className', 'canv');

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 2 });
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const render_context = {
                canvasContext: canvas.getContext('2d'),
                viewport: viewport,
            };
            await page.render(render_context).promise;
            let img = canvas.toDataURL('image/png');
            imagesList.push(img);
        }
        setImages(imagesList);
        setLoadingPdf(false);
    };

    useEffect(() => {
        if (pdf) {
            getImages();
        }
    }, [pdf]);

    useEffect(() => {
        if (file && file.url) {
            if (file.name && isPdf(file.name)) {
                setLoadingPdf(true);
                pdfjsLib.getDocument(file.url).promise.then((pdfDoc_: any) => {
                    setPdf(pdfDoc_);
                });
            } else {
                setImages([file.url]);
            }
        }
    }, [file]);

    const handleLightBoxOpen = () => {
        setLightBoxState(true);
    };

    const handleLightBoxClose = () => {
        setImageIndex(0);
        setLightBoxState(false);
    };

    const handleDownload = () => {
        if (file?.url) {
            window.open(file.url, '_blank');
        }
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 1,
                alignItems: 'center',
            }}
        >
            <LoadingButton
                variant='outlined'
                sx={{
                    display: 'grid',
                    gridTemplateRows: 'repeat(2, auto)',
                    gap: 1,
                    placeItems: 'center',
                    placeContent: 'center',
                    width: '100%',
                    minWidth: 150,
                    height: 120,
                    fontWeight: 700,
                    textAlign: 'center',
                    fontSize: 13,
                    '& .MuiButton-startIcon': {
                        m: 0,
                        '.MuiSvgIcon-root': {
                            fontSize: 30,
                        },
                    },
                    p: 1,
                }}
                onClick={handleLightBoxOpen}
                startIcon={<InsertDriveFileOutlinedIcon />}
                loading={isLoadingFile || loadingPdf}
                disabled={file === undefined}
            >
                <Box>
                    {label}

                    {subLabel && (
                        <Typography
                            sx={{
                                fontSize: 13,
                            }}
                        >
                            {subLabel}
                        </Typography>
                    )}
                </Box>
            </LoadingButton>

            {lightBoxState && !loadingPdf && (
                <Lightbox
                    mainSrc={images[imageIndex]}
                    nextSrc={images[(imageIndex + 1) % images.length]}
                    prevSrc={images[(imageIndex + images.length - 1) % images.length]}
                    onCloseRequest={handleLightBoxClose}
                    onMovePrevRequest={() => setImageIndex((imageIndex + images.length - 1) % images.length)}
                    onMoveNextRequest={() => setImageIndex((imageIndex + 1) % images.length)}
                    toolbarButtons={[
                        <IconButton key='download' style={{ color: 'white' }} onClick={handleDownload}>
                            <DownloadOutlinedIcon />
                        </IconButton>,
                    ]}
                />
            )}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateRows: 'repeat(2, auto)',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                {withUploadButton && (
                    <IconButton color='primary' onClick={handleUploadDocumentDialogOpen}>
                        <FileUploadIcon />
                    </IconButton>
                )}

                <IconButton color='primary' onClick={handleDownload} disabled={file === undefined}>
                    <FileDownloadIcon />
                </IconButton>
            </Box>

            {uploadDocumentDialogState && tag && refetch && (
                <UploadDocumentDialog
                    open={uploadDocumentDialogState}
                    tag={tag}
                    onClose={handleUploadDocumentDialogClose}
                    refetch={refetch}
                />
            )}
        </Box>
    );
};

export default DocumentBoxItem;
